import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { releaseCampaignService } from '@services/enhancedApiService'
import {
  DISABLED_RELEASE_CAMPAIGN,
  getReleaseCampaignRefreshDelay,
  normalizeReleaseCampaign,
  RELEASE_CAMPAIGN_LOAD_STATUS
} from '@utils/releaseCampaign'

const ReleaseCampaignContext = createContext({
  campaign: DISABLED_RELEASE_CAMPAIGN,
  error: null,
  isLoading: true,
  isResolved: false,
  refreshCampaign: async () => null,
  status: RELEASE_CAMPAIGN_LOAD_STATUS.LOADING
})

export function ReleaseCampaignProvider({ children }) {
  const [campaign, setCampaign] = useState(DISABLED_RELEASE_CAMPAIGN)
  const [error, setError] = useState(null)
  const [hasSuccessfulConfig, setHasSuccessfulConfig] = useState(false)
  const [status, setStatus] = useState(RELEASE_CAMPAIGN_LOAD_STATUS.LOADING)
  const mountedRef = useRef(false)
  const lastSuccessfulCampaignRef = useRef(null)
  const requestIdRef = useRef(0)
  const refreshDelayRef = useRef(null)

  if (refreshDelayRef.current === null) {
    refreshDelayRef.current = getReleaseCampaignRefreshDelay(Math.random())
  }

  const refreshCampaign = useCallback(async () => {
    const requestId = ++requestIdRef.current

    try {
      const response = await releaseCampaignService.getReleaseCampaign()
      const nextCampaign =
        response?.enabled === false
          ? DISABLED_RELEASE_CAMPAIGN
          : normalizeReleaseCampaign(response)

      if (response?.enabled !== false && !nextCampaign.enabled) {
        throw new Error('Invalid release campaign response')
      }

      if (mountedRef.current && requestId === requestIdRef.current) {
        lastSuccessfulCampaignRef.current = nextCampaign
        setCampaign(nextCampaign)
        setError(null)
        setHasSuccessfulConfig(true)
        setStatus(RELEASE_CAMPAIGN_LOAD_STATUS.READY)
      }

      return nextCampaign
    } catch (refreshError) {
      if (mountedRef.current && requestId === requestIdRef.current) {
        setError(refreshError)
        setStatus(RELEASE_CAMPAIGN_LOAD_STATUS.ERROR)
      }

      return lastSuccessfulCampaignRef.current
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true
    refreshCampaign()

    const handleScheduledRefresh = () => {
      if (document.visibilityState !== 'visible') return undefined
      return refreshCampaign()
    }
    const intervalId = window.setInterval(
      handleScheduledRefresh,
      refreshDelayRef.current
    )
    const handleFocus = () => refreshCampaign()

    window.addEventListener('focus', handleFocus)

    return () => {
      mountedRef.current = false
      requestIdRef.current += 1
      window.clearInterval(intervalId)
      window.removeEventListener('focus', handleFocus)
    }
  }, [refreshCampaign])

  const value = useMemo(
    () => ({
      campaign,
      error,
      isLoading: status === RELEASE_CAMPAIGN_LOAD_STATUS.LOADING,
      isResolved: hasSuccessfulConfig,
      refreshCampaign,
      status
    }),
    [campaign, error, hasSuccessfulConfig, refreshCampaign, status]
  )

  return (
    <ReleaseCampaignContext.Provider value={value}>
      {children}
    </ReleaseCampaignContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useReleaseCampaign() {
  return useContext(ReleaseCampaignContext)
}
